'use client'

import { css } from '@lastbrand/web-ui-styled-system/css'
import { useTranslation } from 'next-i18next'
import React from 'react'

import { TEST_LOCATORS } from '@src/constants/test-locators'
import DOBModal from '@src/quince-components/src/atoms/dobModal'
import { ProductFormSubscriptionPurchaseSection } from '@src/quince-components/src/atoms/productForm/components/ProductFormSubscriptionPurchaseSection'
import Product from '@src/quince-site-common/src/domains/Product'
import productHelper, { isProductAlcohol } from '@src/quince-site-common/src/helpers/productHelper'
import useRecurringItemSubscriptionsExperiment from '@src/quince-site-common/src/hooks/useRecurringItemSubscriptionsExperiment'
import { getSanitizedHtml } from '@src/quince-site-common/src/utils/getSanitizedHtml'
import { TPDPSectionData } from '@src/quince-site-pdp/src/types/pdp-section-data.type'

import { MiniPDPProductCTADS } from '../miniPDPProductCTA/mini-pdp-product-cta-ds'
import { MiniPDPProductInfoDS } from '../miniPDPProductInfo/mini-pdp-product-info-ds'
import { MiniPDPProductOptionsDS } from '../miniPDPProductOptions/mini-pdp-product-options-ds'

import { useMiniPDPProductPurchase } from './hooks/useMiniPDPProductPurchase'

const wrapperCss = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'space-24',
  marginTop: { base: 'space-16', lg: 'space-32' },
  marginLeft: { base: 0, lg: 'space-120' },
  width: { base: 'auto', lg: '480px' },
  // the subscription wrapper below can render nothing; hide it so flex gap skips the empty slot
  '& > *:empty': { display: 'none' },
})

const informationBoxCss = css({
  width: 'inherit',
  textAlign: 'left',
  padding: 'space-20',
  backgroundColor: 'paperwhite',
  textStyle: 'body-14',
})

const additionalMessageCss = css({ paddingTop: 'space-10' })

// The selector/shimmer bring their own marginBlock (needed in block-layout parents like the full PDP form);
// here the flex gap already spaces them, so zero the margins to avoid doubled spacing.
const subscriptionSectionCss = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'space-24',
  '& > *': { marginBlock: 0 },
})

export const MiniPDPProductPurchaseDS = ({
  product,
  sectionData,
}: {
  product: Product
  sectionData: TPDPSectionData
}) => {
  const { t } = useTranslation('common')

  const {
    selectedOptions,
    productOptions,
    onOptionSelect,
    aboveFoldTrackingRefs,
    selectedSiblingProductId,
    productPurchaseInfo,
    sizeOptionRef,
    selectedVariant,
    selectedVariantPurchaseInfo,
    onAddToCart,
    onJoinTheWaitList,
    productCTAType,
    areAllVariantOOS,
    productFormErrorMessage,
    inputOptionError,
    selectedPlanId,
    purchaseOption,
    showPurchaseTypeSelector,
    isAddToCartInProgress,
    showErrorMessage,
    showProductSubscriptionDeliveryMessage,
    variantSubscriptionPlans,
    formattedSubscriptionPrice,
    formattedSalePrice,
    formattedTraditionalPrice,
    formattedFestivalOfferPrice,
    savingPercentage,
    festivalPricePerUnit,
    subscriptionOfferPricePerUnit,
    subscriptionFestivalSavingPercentage,
    setPurchaseOption,
    setSelectedPlanId,
    selectedVariantAvailableInventory,
    showVariantAvailableInventory,
    isExpandedSize,
    expandSizeMiniPDP,
    collapseSizeMiniPDP,
    isDOBModalOpen,
    dateOfBirth,
    setDateOfBirth,
    dobErrorMessage,
    setDobErrorMessage,
    disableAgeVerification,
    handleDOBModalSubmit,
    handleDOBModalClose,
    isReturnable,
    brandName,
    brandLink,
    disableAddToCart,
    isCurrentProductOptionLeavingSoon,
    onClickedShopSimilar,
  } = useMiniPDPProductPurchase({ product, sectionData })

  const isRecurringItemSubscriptionsExpEnabled = useRecurringItemSubscriptionsExperiment()

  const isAgeRestrictedProduct = productHelper.getIsAgeRestrictedProduct(product)
  const pdpCustomMoreInfoText = productHelper.getPDPCustomMoreInfoText(product)

  return (
    <div className={wrapperCss} data-testid={TEST_LOCATORS.MINI_PDP.PRODUCT_PURCHASE}>
      <MiniPDPProductInfoDS
        product={product}
        selectedOptions={selectedOptions}
        selectedVariant={selectedVariant}
        brandName={brandName}
        brandLink={brandLink}
        formattedSubscriptionPrice={formattedSubscriptionPrice}
        formattedSalePrice={formattedSalePrice}
        formattedTraditionalPrice={formattedTraditionalPrice}
        subscriptionOfferPricePerUnit={subscriptionOfferPricePerUnit}
        savingPercentage={savingPercentage}
        formattedFestivalOfferPrice={formattedFestivalOfferPrice}
        festivalPricePerUnit={festivalPricePerUnit}
        purchaseOption={purchaseOption}
      />
      <MiniPDPProductOptionsDS
        product={product}
        productOptions={productOptions}
        selectedOptions={selectedOptions}
        aboveFoldTrackingRefs={aboveFoldTrackingRefs}
        onOptionSelect={onOptionSelect}
        selectedSiblingProductId={selectedSiblingProductId}
        productPurchaseInfo={productPurchaseInfo}
        inputOptionError={inputOptionError}
        sizeOptionRef={sizeOptionRef}
        isExpandedSize={isExpandedSize}
        expandSizeMiniPDP={expandSizeMiniPDP}
        collapseSizeMiniPDP={collapseSizeMiniPDP}
      />

      {pdpCustomMoreInfoText && isAgeRestrictedProduct && (
        <div className={informationBoxCss}>
          <div
            //eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: getSanitizedHtml(pdpCustomMoreInfoText) }}
          />
          {isProductAlcohol(product) && (
            <div className={additionalMessageCss}>{`${t('not_returnable_food_safety')}.`}</div>
          )}
        </div>
      )}
      {!isReturnable && !isAgeRestrictedProduct && (
        <div className={informationBoxCss}>{t('this_product_is_not_returnable')}</div>
      )}

      <div className={subscriptionSectionCss}>
        <ProductFormSubscriptionPurchaseSection
          showProductSubscriptionDeliveryMessage={showProductSubscriptionDeliveryMessage}
          subscribedPlan={product?.subscription?.subscribedPlan}
          selectedVariantPurchaseInfoShowWaitList={selectedVariantPurchaseInfo?.showWaitList}
          productCTAType={productCTAType}
          isRecurringItemSubscriptionsExpEnabled={isRecurringItemSubscriptionsExpEnabled}
          subscriptionEnabled={product?.subscriptionEnabled}
          productPurchaseInfo={productPurchaseInfo}
          showPurchaseTypeSelector={showPurchaseTypeSelector}
          product={product}
          purchaseOption={purchaseOption}
          setPurchaseOption={setPurchaseOption}
          selectedPlanId={selectedPlanId}
          setSelectedPlanId={setSelectedPlanId}
          isFromCartPopup={false}
          isBuyNow={false}
          variantSubscriptionPlans={variantSubscriptionPlans}
          formattedSubscriptionPrice={formattedSubscriptionPrice}
          formattedSalePrice={formattedSalePrice}
          formattedFestivalOfferPrice={formattedFestivalOfferPrice}
          subscriptionFestivalSavingPercentage={subscriptionFestivalSavingPercentage}
          subscriptionId={product?.subscription?.subscribedPlan?.subscriptionId}
          showVariantAvailableInventory={showVariantAvailableInventory}
          selectedVariantAvailableInventory={selectedVariantAvailableInventory}
        />
      </div>
      <MiniPDPProductCTADS
        product={product}
        selectedOptions={selectedOptions}
        selectedVariantPurchaseInfo={selectedVariantPurchaseInfo}
        productCTAType={productCTAType}
        areAllVariantOOS={areAllVariantOOS}
        inputOptionError={inputOptionError}
        productFormErrorMessage={showErrorMessage ? productFormErrorMessage : ''}
        onAddToCart={onAddToCart}
        onJoinTheWaitList={onJoinTheWaitList}
        selectedPlanId={selectedPlanId}
        purchaseOption={purchaseOption}
        showPurchaseTypeSelector={showPurchaseTypeSelector}
        isAddToCartInProgress={isAddToCartInProgress}
        selectedVariantAvailableInventory={selectedVariantAvailableInventory}
        showVariantAvailableInventory={showVariantAvailableInventory}
        disableAddToCart={disableAddToCart}
        isCurrentProductOptionLeavingSoon={isCurrentProductOptionLeavingSoon}
        onClickedShopSimilar={onClickedShopSimilar}
      />

      {isDOBModalOpen && !disableAgeVerification && (
        <DOBModal
          onClose={handleDOBModalClose}
          dateOfBirth={dateOfBirth}
          setDateOfBirth={setDateOfBirth}
          open={isDOBModalOpen}
          onSubmit={handleDOBModalSubmit}
          loader={isAddToCartInProgress ?? false}
          dobErrorMessage={dobErrorMessage}
          setDobErrorMessage={setDobErrorMessage}
          ageLimit={productHelper.getAgeLimitInYears(product) ?? 0}
        />
      )}
    </div>
  )
}
